// El visor de video del CRM, en UN solo sitio.
//
// Ticket de Gabriel De Silva (2026-09-02): *"el visor de video sale en negro y
// en 0:00"*. No se pudo reproducir aqui — depende del archivo y del navegador
// de quien lo abre —, asi que en vez de adivinar un arreglo se hicieron dos
// cosas comprobables:
//
//  1. QUITAR EL AUTOPLAY. Los tres visores del CRM no eran iguales: el del
//     Centro de Ayuda usa `controls playsInline preload="metadata"` y funciona;
//     los dos que Gabriel reporta usaban `autoPlay` y no traian ni `playsInline`
//     ni `preload`. Un `autoPlay` CON SONIDO lo bloquea el navegador por norma
//     — Chrome, Safari y Firefox — y lo que queda en pantalla es exactamente
//     eso: un recuadro negro parado en 0:00, sin ningun error. Con `preload
//     ="metadata"` el navegador trae el primer fotograma y lo pinta aunque
//     nadie le de a play, y `playsInline` evita que el iPhone se lo lleve a
//     pantalla completa.
//
//  2. QUE EL VISOR DIGA QUE PASA. Un <video> que falla no enseña nada: se queda
//     negro. Ahora se lee el codigo de error del navegador y se dice en
//     cristiano, con el boton de descargar al lado. Si vuelve a pasar, la
//     captura de pantalla ya trae el diagnostico en vez de "sale en negro".
//     Es el mismo criterio que ya se uso con las fotos HEIC de iPhone.
//
// El sospechoso numero uno de un 0:00 en negro CON el autoplay ya quitado es un
// archivo que el navegador no sabe decodificar: un .mov o .mkv, o un .mp4 de
// iPhone grabado en HEVC. Windows + Chrome no reproduce ninguno de los tres.
// Eso es lo que dira el mensaje.

import { useState } from 'react';
import { Download, AlertTriangle } from 'lucide-react';

/**
 * @param {string} src        URL del video (ya firmada por quien llama)
 * @param {'es'|'en'} lang
 * @param {string} [nombre]   nombre del archivo, para el boton de descargar
 * @param {object} [style]    estilos del <video>
 */
export default function VisorDeVideo({ src, lang = 'es', nombre, style }) {
  const es = lang !== 'en';
  const [fallo, setFallo] = useState(null); // {codigo, texto}

  const explicar = (codigo) => {
    // Codigos de MediaError. Se dicen en cristiano y se enseña el numero, para
    // que una captura de pantalla sirva de diagnostico.
    if (codigo === 4 || codigo === 3) {
      return es
        ? 'Tu navegador no sabe reproducir este archivo. Suele pasar con los .mov y .mkv, y con los videos de iPhone grabados en "alta eficiencia" (HEVC): en Windows con Chrome no se ven. Descargalo para verlo, o vuelve a subirlo como .mp4 normal (H.264).'
        : "Your browser can't play this file. It usually happens with .mov and .mkv, and with iPhone videos recorded in “high efficiency” (HEVC): Chrome on Windows won't play them. Download it to watch, or re-upload it as a regular .mp4 (H.264).";
    }
    if (codigo === 2) {
      return es
        ? 'Se corto la conexion con el archivo. Si la ventana llevaba un rato abierta, cierrala y vuelve a abrir el video: el enlace se renueva al abrirlo.'
        : 'The connection to the file dropped. If this window had been open a while, close it and open the video again: the link is renewed on open.';
    }
    return es
      ? 'No se pudo reproducir el video.'
      : 'The video could not be played.';
  };

  if (fallo) {
    return (
      <div style={{ padding: '1.25rem', borderRadius: 10, background: 'rgba(255,183,0,.08)', border: '1px solid rgba(255,183,0,.35)', color: 'var(--c-text,#c8dff0)', fontSize: '.82rem', lineHeight: 1.55, textAlign: 'left', maxWidth: 560 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 800, color: 'var(--c-amber,#ffb700)', marginBottom: '.4rem' }}>
          <AlertTriangle size={15} /> {es ? 'No se puede ver aqui' : "Can't play here"}
        </div>
        <div>{fallo.texto}</div>
        <div style={{ marginTop: '.7rem', display: 'flex', gap: '.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <a href={src} download={nombre || undefined} target="_blank" rel="noopener noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--c-cyan,#00d4ff)', color: '#06121f', borderRadius: 8, padding: '.4rem .8rem', fontSize: '.76rem', fontWeight: 800, textDecoration: 'none' }}>
            <Download size={13} /> {es ? 'Descargar el video' : 'Download the video'}
          </a>
          <span style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono)' }}>
            {es ? 'codigo' : 'code'} {fallo.codigo ?? '?'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <video
      src={src}
      controls
      playsInline
      preload="metadata"
      onError={(e) => {
        const codigo = e?.target?.error?.code ?? null;
        setFallo({ codigo, texto: explicar(codigo) });
      }}
      style={{ background: '#000', ...style }}
    />
  );
}
